import "./index.scss";
import "./reducedMotion.scss";

import { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Provider, useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";

import store from "./Store";
import {
  selectFirebaseSheetsPulled,
  selectGameData,
  selectGoldStar,
  selectVocabData,
  getData,
  findInitialPlayerLevelOnLoad,
  readBestLevel,
} from "./Store/store";
import App from "./App";
import LoadingScreen from "./Components/LoadingScreen";
import ErrorBoundary from "./Components/UI/ErrorBoundary";
import {
  buildScoreData,
  countStars,
  mergeSavedScores,
} from "./utils/scoreData";
import config from "./config";

import PlayerLevelData from "./data/playerLevel.json";
import { levelFloor } from "./utils/playerLevel";

document.title = config.AppName;

let checkDataArray = [];
let gameList = [];

const setInitialLevelData = (
  gameData,
  setInitialLevel,
  levelData,
  setLevelData,
) => {
  let newLevelData = levelData;
  if (
    localStorage[`KZ_${config.Language}_LevelData`] &&
    localStorage[`KZ_${config.Language}_LevelData`].length > 2
  ) {
    newLevelData = JSON.parse(localStorage[`KZ_${config.Language}_LevelData`]);
    if (newLevelData.type === "string") {
      newLevelData = JSON.parse(newLevelData);
    }
  } else {
    let gameTypes = config.PossibleGames;
    gameTypes.forEach((gameType) => {
      if (
        gameType !== "Bingo" &&
        gameType !== "Fill In The Blank" &&
        gameType !== "Story Book" &&
        gameType !== "Crocodile" &&
        gameType !== "Solve It 2" &&
        gameType !== "Solve It" &&
        gameType !== "Verb Activity"
      ) {
        if (!gameList.includes(gameType)) {
          gameList.push(gameType);
        }
      }
    });
    gameList.forEach((gameType) => {
      let cats = ["2", "3", "4", "5"];
      cats.forEach((cat) => {
        newLevelData.push({
          gameType: `${cat} Categories`,
          spreadGameType: gameType,
          category: [],
          levelEasy: 0,
          levelMedium: 0,
          levelHard: 0,
          categoryEasy: [],
          categoryMedium: [],
          categoryHard: [],
        });
      });
    });
    let dataToRun = gameData.filter((d) => isNaN(d.Key.length));
    dataToRun.forEach((game) => {
      gameList.forEach((gameType) => {
        if (!checkDataArray.includes(`${gameType}${game.category}`)) {
          checkDataArray.push(`${gameType}${game.category}`);
          newLevelData.push({
            gameType: gameType,
            spreadGameType: gameType,
            category: game.category,
            levelEasy: 0,
            levelMedium: 0,
            levelHard: 0,
            categoryEasy: [],
            categoryMedium: [],
            categoryHard: [],
          });
        }
      });
    });
  }
  setLevelData(newLevelData);
  setInitialLevel(true);
};

const setInitialScoreData = ({
  data,
  GoldStar,
  setInitialScore,
  dispatch,
  setScoreData,
}) => {
  const fresh = buildScoreData(data, GoldStar, config.PossibleGames);
  let saved = [];
  if (
    localStorage[`KZ_${config.Language}_ScoreData`] &&
    localStorage[`KZ_${config.Language}_ScoreData`].length > 2
  ) {
    saved = JSON.parse(localStorage[`KZ_${config.Language}_ScoreData`]);
    if (typeof saved === "string") {
      saved = JSON.parse(saved);
    }
  }
  const scoreData =
    saved.length > 0
      ? mergeSavedScores(fresh.scoreData, saved)
      : fresh.scoreData;
  // A first visit has always counted toward the total every game the data
  // mentions, even ones not turned on yet (buildScoreData's total); kept as is.
  const { currentStars, totalStars } =
    saved.length > 0
      ? countStars(scoreData)
      : { currentStars: 0, totalStars: fresh.totalStars };
  // counted from the saved list, before any new game is merged into it
  const bestLevel = levelFloor({
    savedBest: readBestLevel(),
    savedStars: saved.length > 0 ? countStars(saved) : null,
    levels: PlayerLevelData,
  });
  dispatch(findInitialPlayerLevelOnLoad(currentStars, totalStars, bestLevel));
  setScoreData(scoreData);
  setInitialScore(true);
};

const saveData = (score, level) => {
  localStorage.setItem(
    `KZ_${config.Language}_ScoreData`,
    JSON.stringify(score),
  );
  localStorage.setItem(
    `KZ_${config.Language}_LevelData`,
    JSON.stringify(level),
  );
};

const SendData = () => {
  const dispatch = useDispatch();
  const firebaseSheetsPulled = useSelector(selectFirebaseSheetsPulled);
  const gameData = useSelector(selectGameData);
  const goldStar = useSelector(selectGoldStar);
  const vocabData = useSelector(selectVocabData);
  const [initialScore, setInitialScore] = useState(false);
  const [initialLevel, setInitialLevel] = useState(false);
  const [levelData, setLevelData] = useState([]);
  const [scoreData, setScoreData] = useState([]);
  useEffect(() => {
    config.SheetsToPull.forEach((d) => {
      dispatch(getData(d.collection, d.nameToSave));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allSheetsPulled =
    firebaseSheetsPulled?.length > config.SheetsToPull.length;
  // Every sheet "loaded", but the ones the games can't work without are empty:
  // NACA-Core timed out and Firebase couldn't be reached either. Say so,
  // instead of opening an app with no games (every level reading 0/1).
  const dataMissing =
    allSheetsPulled &&
    (vocabData.length === 0 || gameData.length === 0);

  useEffect(() => {
    if (allSheetsPulled && !dataMissing) {
      if (initialScore === false) {
        setInitialScoreData({
          data: gameData,
          GoldStar: goldStar,
          setInitialScore: setInitialScore,
          dispatch: dispatch,
          setScoreData: setScoreData,
        });
      }
      if (initialLevel === false) {
        setInitialLevelData(gameData, setInitialLevel, levelData, setLevelData);
      }
    }
  }, [
    allSheetsPulled,
    dataMissing,
    initialScore,
    initialLevel,
    gameData,
    goldStar,
    levelData,
    dispatch,
  ]);

  if (dataMissing) {
    return <LoadingScreen failed />;
  }
  if (allSheetsPulled) {
    return (
      <Router>
        <App
          style={{ width: "100%", height: "100%", backgroundColor: "red" }}
          scoreDataProp={scoreData}
          levelDataProp={levelData}
          saveData={saveData}
          config={config}
        />
      </Router>
    );
  } else {
    return <LoadingScreen />;
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorBoundary fullPage>
    <Provider store={store}>
      <SendData />
    </Provider>
  </ErrorBoundary>,
);
// root.render(
//   <React.StrictMode>
//     <Provider store={store}>
//       <SendData />
//     </Provider>
//   </React.StrictMode>
// );
